import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { JwtHelperService } from '@auth0/angular-jwt'
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subscription,
  throwError,
  timer,
} from 'rxjs'
import {
  catchError,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators'

import { Enums, Profile } from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../environments/verity-environment.service'

const REFRESH_STORAGE_KEY = 'hunter-auth-refresh-token'
const ACCESS_STORAGE_KEY = 'hunter-auth-access-token'
const LOGIN_REDIRECT_STORAGE_KEY = 'hunter-login-redirect-path'

export enum LOGOUT_REASON {
  EXPIRED = 'expired',
  REVOKED = 'revoked',
}

export interface PasswordResponse {
  message: string
}

export interface Tokens {
  accessToken: string
  refreshToken: string
}

export type LoginResponse =
  | {
      tokens: Tokens
      user: Profile
    }
  | {
      samlLoginUrl: string
    }
  | {
      hasVerityAccess: boolean
    }

export interface RefreshResponse {
  tokens: Tokens
}

export interface TokenPayload {
  user: Profile
  iat: number
  exp: number
}

export interface VerityAuth0Tokens {
  accessToken: string
  idTokenClaims?: unknown
}

interface AuthState {
  initialized: boolean
  isAuthenticated: boolean
}

@Injectable()
export class AuthService {
  private httpClient = inject(HttpClient)
  private router = inject(Router)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  public accessToken$: ReplaySubject<string> = new ReplaySubject(1)
  public user$: BehaviorSubject<Profile | null> = new BehaviorSubject(null)
  public isIframe = window.self !== window.top

  private jwtHelper: JwtHelperService
  private refreshHttp: Observable<Tokens>
  private refreshTimer: Subscription

  private readonly defaultLoginRedirectURL: string = '/research'

  public readonly logoutRedirectURL: string = '/login'

  private authStateSubject = new BehaviorSubject<AuthState>({
    initialized: false,
    isAuthenticated: false,
  })

  authState$ = this.authStateSubject.asObservable()

  constructor() {
    this.jwtHelper = new JwtHelperService()

    const { accessToken, refreshToken } = this.loadTokens()
    const allowedOrigins =
      this.verityEnvironmentService.verityConfig.allowedOrigins || []

    if (refreshToken) {
      if (this.jwtHelper.isTokenExpired(refreshToken)) {
        console.warn(
          'Hunter: AuthService: Refresh token is expired, logging out...',
        )
        this.handleInvalidTokens(LOGOUT_REASON.EXPIRED)
      }
    }

    if (accessToken) {
      this.startAutoRefreshTimer(new Date())
    } else {
      // If no token, don't mark as initialized yet
      // We'll do it later when tokens are received or determined.
      if (!this.isIframe) {
        this.authStateSubject.next({
          initialized: true,
          isAuthenticated: false,
        })
      }
    }

    // If we're in an iframe, don't mark as initialized just yet; await token update
    // 🌐 PostMessage listener (iframe only)
    if (this.isIframe) {
      this.authStateSubject.next({
        initialized: false,
        isAuthenticated: false,
      })

      window.addEventListener(
        'message',
        (event) => {
          if (!allowedOrigins.includes(event.origin)) {
            console.warn(
              'Hunter: AuthService: Ignored message from unknown origin:',
              event.origin,
            )
            console.warn(
              'Hunter: AuthService: Accepted origins:',
              allowedOrigins,
            )
            return
          }

          if (event.data?.type === 'auth' && event.data.tokens) {
            const { accessToken } = event.data.tokens

            this.auth0SilentLogin({ accessToken }).subscribe({
              next: (response) => {
                if ('tokens' in response) {
                  this.setTokens(response.tokens)
                } else {
                  console.warn(
                    'Hunter: AuthService: Unexpected silent login response',
                    response,
                  )
                }
              },
              error: (err) => {
                console.error('Hunter: AuthService: Silent login failed:', err)
                this.handleInvalidTokens(LOGOUT_REASON.REVOKED)
              },
            })
          }
        },
        false,
      )
    }
  }

  /**
   * Checks that the user has the correct tokens to be authenticated.
   */
  public isAuthenticated(): boolean {
    const { refreshToken } = this.loadTokens()
    return !this.jwtHelper.isTokenExpired(refreshToken)
  }

  /**
   * Fetch new refresh and access tokens. Will politely handle multiple calls,
   * but only perform one HTTP request at a time.
   */
  public refreshTokens(): Observable<Tokens> {
    const { refreshToken } = this.loadTokens()

    if (!refreshToken) {
      // if there is no stored refresh token, then we can not get a new refresh
      return throwError(() => new Error('No valid token stored'))
    }

    if (this.jwtHelper.isTokenExpired(refreshToken)) {
      console.warn(
        'Hunter: AuthService: Refresh token is expired, logging out...',
      )
      this.handleInvalidTokens(LOGOUT_REASON.EXPIRED)
    }

    if (this.refreshHttp == null) {
      this.refreshHttp = this.httpClient
        .post<RefreshResponse>(
          `${this.verityEnvironmentService.apiUrl}/auth/refresh`,
          {
            refreshToken,
          },
        )
        .pipe(
          catchError((error) => {
            // Catch failed refresh due to revoked refresh token
            if (error instanceof HttpErrorResponse && error.status === 401) {
              this.handleInvalidTokens(LOGOUT_REASON.REVOKED)
            } else {
              return throwError(() => error)
            }
          }),
          map((resp) => resp.tokens),
          tap((tokens) => {
            this.clearTokens()
            this.setTokens(tokens)
          }),
          finalize(() => {
            this.refreshHttp = null
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        )
    }

    return this.refreshHttp
  }

  /**
   * Saves tokens to session storage and public observables
   * Also starts the timeout countdown for auto logout
   * @param tokens
   */
  public setTokens(tokens: Tokens): void {
    localStorage.setItem(REFRESH_STORAGE_KEY, tokens.refreshToken)
    localStorage.setItem(ACCESS_STORAGE_KEY, tokens.accessToken)

    const decoded: TokenPayload = this.jwtHelper.decodeToken(tokens.accessToken)

    this.accessToken$.next(tokens.accessToken)
    this.user$.next(decoded.user)

    this.authStateSubject.next({
      initialized: true,
      isAuthenticated: true,
    })

    this.startAutoRefreshTimer()
  }

  private startAutoRefreshTimer(refreshTime?: Date): void {
    const { accessToken } = this.loadTokens()
    const decoded: TokenPayload = this.jwtHelper.decodeToken(accessToken)

    const expires = refreshTime
      ? refreshTime.getTime()
      : (decoded?.exp ?? 0) * 1000
    const now = new Date().getTime()

    const timerDuration = Math.max((expires - now) / 2, 0)

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe()
    }

    this.refreshTimer = timer(timerDuration)
      .pipe(switchMap(() => this.refreshTokens()))
      .subscribe()
  }

  private clearTokens(): void {
    localStorage.removeItem(REFRESH_STORAGE_KEY)
    localStorage.removeItem(ACCESS_STORAGE_KEY)
  }

  private handleInvalidTokens(reason?: LOGOUT_REASON): void {
    this.clearTokens()
    this.user$.next(null)

    if (!this.isIframe) {
      this.logout(reason)
    } else {
      // Log the reason for invalidating tokens in iframe context for audit trail
      console.warn(
        '[AuthService] Tokens invalidated in iframe context.',
        reason ? `Reason: ${reason}` : 'No reason provided.',
      )
    }
  }

  public logout(source?: LOGOUT_REASON): void {
    this.setLoginRedirectPath(this.defaultLoginRedirectURL)

    window.location.href = `${this.logoutRedirectURL}${
      source ? '?source=' + source : ''
    }`
  }

  public login(email: string, password?: string): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(
      `${this.verityEnvironmentService.apiUrl}/auth/login`,
      { email, password },
    )
  }

  public auth0SilentLogin(
    verityAuth0Tokens: VerityAuth0Tokens,
  ): Observable<LoginResponse> {
    const { accessToken } = verityAuth0Tokens

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
    })

    return this.httpClient.get<LoginResponse>(
      `${this.verityEnvironmentService.apiUrl}/auth/auth0-silent-login`,
      { headers },
    )
  }

  public forgotPassword(email: string): Observable<PasswordResponse> {
    const url = `${this.verityEnvironmentService.apiUrl}/auth/forgot-password`
    return this.httpClient.post<PasswordResponse>(url, { email })
  }

  public resetPassword(
    resetToken: string,
    password: string,
  ): Observable<PasswordResponse> {
    const url = `${this.verityEnvironmentService.apiUrl}/auth/reset-password/${resetToken}`
    return this.httpClient.post<PasswordResponse>(url, { password })
  }

  private loadTokens(): Tokens {
    return {
      accessToken: localStorage.getItem(ACCESS_STORAGE_KEY) ?? '',
      refreshToken: localStorage.getItem(REFRESH_STORAGE_KEY) ?? '',
    }
  }

  public setLoginRedirectPath(path: string): void {
    localStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, path)
  }

  public getLoginRedirectPath(clearStoredPath = false): string {
    const path =
      localStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY) ??
      this.defaultLoginRedirectURL

    if (clearStoredPath) {
      localStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
    }

    return path
  }

  public checkStatus(redirectRoute: string): void {
    // If user is expired or revoked, redirect
    this.user$
      .pipe(
        filter((user) => !!user),
        take(1),
      )
      .subscribe((user) => {
        if (
          user.company.status === Enums.CompanyStatus.EXPIRED ||
          user.company.status === Enums.CompanyStatus.REVOKED
        ) {
          this.router.navigateByUrl(redirectRoute)
          return
        }
      })
  }
}
